import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    utils as XLSXUtils,
    writeFile as XLSXWriteFile,
    WorkSheet
} from 'xlsx';

// Ensure plugins are available (main.tsx also extends these globally)
dayjs.extend(utc);
dayjs.extend(timezone);

/** Matches dates without a time component, e.g. "2026-02-19" */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** Matches datetime strings that include a time component, e.g. "2026-02-19T14:30:00" */
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T/;

/**
 * Normalises a single cell value for Excel export.
 *
 * Date handling:
 * - dayjs objects              → `.toDate()` — already carries tz info; Excel shows as date
 * - Date-only ISO strings      → native `Date` (UTC midnight) — enables Excel's hierarchical
 *                                year › month › day filter tree
 * - DateTime ISO strings       → `"YYYY-MM-DD HH:mm:ss"` string in company timezone —
 *                                time is fully visible & the format is lexicographically sortable
 *
 * Other types:
 * - Arrays  → comma-separated string (prevents "[object Object]")
 * - Objects → JSON string            (prevents "[object Object]")
 * - Rest    → returned as-is
 *
 * @param value - The raw cell value
 * @param tz    - IANA timezone for datetime strings (e.g. "Europe/Copenhagen").
 *               Defaults to the dayjs global default set by `useFormatting`.
 */
export function normalizeExcelCellValue(value: unknown, tz?: string): unknown {
    // dayjs / moment-like objects (have a .format() method) — already carry tz info
    if (
        value &&
        typeof value === 'object' &&
        typeof (value as { format?: unknown }).format === 'function'
    ) {
        return (value as dayjs.Dayjs).toDate();
    }

    if (typeof value === 'string') {
        // Date-only ("2026-02-19"): return native Date (UTC midnight).
        // xlsx writes it as a date serial → Excel shows exact calendar date
        // and enables the hierarchical year › month › day filter tree.
        if (ISO_DATE_ONLY.test(value)) {
            const parsed = dayjs.utc(value);
            if (parsed.isValid()) return parsed.toDate();
        }

        // DateTime ("2026-02-19T05:20:22Z" etc.): return a formatted string
        // in the company timezone so the time component is fully visible.
        // "YYYY-MM-DD HH:mm:ss" is lexicographically sortable and universally
        // readable by Excel, while preserving seconds precision.
        if (ISO_DATETIME.test(value)) {
            const parsed = tz ? dayjs.tz(value, tz) : dayjs.tz(value);
            if (parsed.isValid()) return parsed.format('YYYY-MM-DD HH:mm:ss');
        }
    }

    // Arrays (e.g. posJournalAmounts: [{ posJournalId, amount }, …])
    if (Array.isArray(value)) {
        return value
            .map((item) =>
                item !== null && typeof item === 'object'
                    ? JSON.stringify(item)
                    : String(item)
            )
            .join(', ');
    }

    // Any remaining plain object
    if (value !== null && typeof value === 'object') {
        return JSON.stringify(value);
    }

    return value;
}

export interface ExcelExportOptions {
    /** Keys to exclude from the exported columns. */
    ignoreFields?: string[];
    /**
     * IANA timezone used when converting datetime strings to native Dates.
     * Defaults to the dayjs global default (set by `useFormatting` via
     * `dayjs.tz.setDefault(company.ianaTimeZone)`).
     * Pass `company?.ianaTimeZone` explicitly for clarity.
     */
    timezone?: string;
}

/**
 * Converts an array of objects into an xlsx WorkSheet with:
 * - Bold header row
 * - All cell values normalised via `normalizeExcelCellValue`
 * - Optional field exclusion via `options.ignoreFields`
 * - Timezone-aware date conversion via `options.timezone`
 */
export function buildExcelSheet<T extends Record<string, any>>(
    rows: T[],
    options: ExcelExportOptions = {}
): WorkSheet {
    const { ignoreFields = [], timezone } = options;
    const sheet = XLSXUtils.aoa_to_sheet([]);

    const allHeaders = rows.length > 0 ? Object.keys(rows[0]) : [];
    const headers = allHeaders.filter((h) => !ignoreFields.includes(h));

    XLSXUtils.sheet_add_aoa(sheet, [headers], { origin: 'A1' });

    // Bold header cells
    headers.forEach((header, idx) => {
        const cellAddress = XLSXUtils.encode_cell({ r: 0, c: idx });
        if (!sheet[cellAddress]) sheet[cellAddress] = { t: 's', v: header };
        sheet[cellAddress].s = { font: { bold: true } };
    });

    if (rows.length > 0) {
        const dataRows = rows.map((row) =>
            headers.map((key) => normalizeExcelCellValue(row[key], timezone))
        );
        XLSXUtils.sheet_add_aoa(sheet, dataRows, { origin: 'A2' });
    }

    return sheet;
}

/**
 * Builds an xlsx workbook from `rows` and triggers a browser download.
 *
 * @param rows       Array of plain objects (all keys become column headers)
 * @param sheetName  Name of the worksheet tab
 * @param fileName   Downloaded file name (should end in .xlsx)
 * @param options    Optional config, e.g.:
 *   `{ ignoreFields: ['posJournalAmounts'], timezone: company?.ianaTimeZone }`
 */
export function downloadExcel<T extends Record<string, any>>(
    rows: T[],
    sheetName: string,
    fileName: string,
    options: ExcelExportOptions = {}
): void {
    const sheet = buildExcelSheet(rows, options);
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, sheet, sheetName);
    XLSXWriteFile(workbook, fileName, { bookType: 'xlsx', type: 'binary' });
}
